// ─────────────────────────────────────────────────────────────────────
// /admin/access — Access Management. Built in Batch A (Rounds 3 & 4, 2026).
//
// A single IT-Admin page that consolidates role and permission
// administration into tabs:
//
//   • Roles                  (Round 3) — create / rename / enable-disable /
//                                          safe-delete roles
//   • Role Permissions       (Round 4) — assign/remove permissions (modules)
//                                          for a selected role
//   • Employee Role Assignment (Round 5) — assign/clear a role per employee
//   • Direct User Permissions  (Round 6) — grant/revoke a direct permission
//   • Effective Access Preview (Round 7) — read-only "what can they access & why"
//
// The existing standalone pages (/admin/employee-roles,
// /admin/user-permissions) are intentionally LEFT IN PLACE and untouched;
// these tabs reuse the SAME endpoints. The standalone pages can be retired
// later once the consolidated page is fully validated. Nothing is removed.
//
// Uses the project's own ResponsiveTabs (DashboardKit) for the tab strip —
// same visual language as the rest of the dashboard, no new dependency.
//
// Route guarded by <RoleProtected requireItAdmin> in allRoutes.js. Every
// backend endpoint these tabs call is also [Authorize(Roles="IT Admin")],
// so frontend hiding is defense-in-depth, not the security boundary.
// ─────────────────────────────────────────────────────────────────────
import React, { useState } from "react";
import { Container, Card, CardBody, Alert } from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DashboardPageHeader, ResponsiveTabs } from "../../../Components/Common/DashboardKit";
import RolesTab from "./RolesTab";
import RolePermissionsTab from "./RolePermissionsTab";
import EmployeeAssignmentTab from "./EmployeeAssignmentTab";
import DirectPermissionsTab from "./DirectPermissionsTab";
import EffectiveAccessPreviewTab from "./EffectiveAccessPreviewTab";

const TABS = [
    { key: "roles",      label: "Roles",                    icon: "ri-shield-user-line" },
    { key: "rolePerms",  label: "Role Permissions",         icon: "ri-key-2-line" },
    { key: "assignment", label: "Employee Role Assignment", icon: "ri-user-settings-line" },
    { key: "direct",     label: "Direct User Permissions",  icon: "ri-user-add-line" },
    { key: "preview",    label: "Effective Access Preview", icon: "ri-eye-line" },
];

export default function AccessManagement() {
    document.title = "Access Management | SSW Technologies";

    const [activeTab, setActiveTab] = useState("roles");

    // Lifting one piece of state: when a role is selected/created on the Roles
    // tab, the Role Permissions tab should default to it. Kept here so the two
    // tabs stay in sync without prop-drilling through a store.
    const [focusedRoleId, setFocusedRoleId] = useState(null);

    const goToRolePerms = (roleId) => {
        setFocusedRoleId(roleId ?? null);
        setActiveTab("rolePerms");
    };

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-shield-keyhole-line"
                    eyebrow="Administration"
                    title="Access Management"
                    subtitle="Create roles, map modules and permissions to them, assign roles to employees, and preview effective access."
                    tone="rose"
                    breadcrumb={[
                        { label: "Dashboards", to: "/" },
                        { label: "Admin", to: "/admin" },
                        { label: "Access Management" },
                    ]}
                />

                <Alert color="info" className="d-flex align-items-center mb-3">
                    <i className="ri-information-line me-2 fs-18" />
                    <span>
                        Access changes take effect the next time the affected user signs in
                        (or refreshes their session) — their access is cached at login.
                        Ask them to log out and back in if a change doesn't appear right away.
                    </span>
                </Alert>

                <Card className="border-0 shadow-sm">
                    <CardBody>
                        <ResponsiveTabs
                            tabs={TABS}
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            color="primary"
                        />

                        {/* Render only the active tab (preserves the dashboard's
                            "render only active tab" convention). */}
                        {activeTab === "roles" && (
                            <RolesTab onManagePermissions={goToRolePerms} />
                        )}

                        {activeTab === "rolePerms" && (
                            <RolePermissionsTab initialRoleId={focusedRoleId} />
                        )}

                        {activeTab === "assignment" && <EmployeeAssignmentTab />}

                        {activeTab === "direct" && <DirectPermissionsTab />}

                        {activeTab === "preview" && <EffectiveAccessPreviewTab />}
                    </CardBody>
                </Card>
            </Container>

            <ToastContainer closeButton={false} limit={3} />
        </div>
    );
}
